/**
 * React wrapper around the StorageDriver abstraction.
 *
 * Responsibilities:
 *  - Pick a driver at mount (Local vs Firebase; Convex added in Session 2).
 *  - Subscribe to each collection and mirror values into React state so components
 *    re-render on change.
 *  - Poll connectionStatus at a low frequency so UI status indicators update.
 *  - Expose the same `save*` / `updateGameData` / `pushAuditEvent` / `pushAuditSession`
 *    API that consumers already call, delegating to `driver.save` / `driver.updateMultiple`
 *    / `driver.pushAuditEvent` / `driver.pushAuditSession`.
 *
 * The return shape preserves every key the pre-extraction `useStorage` exposed:
 * all 18 collection slices spread into the returned object, plus loading,
 * connectionStatus, plus all 18 save methods and the three composite ops.
 * (Session 10: `useFirebase` removed — Firebase is gone, nothing branches on it.)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildInitialData, shouldForceLocalMode } from './StorageDriver.js';
import { createLocalDriver } from './createLocalDriver.js';
import { createConvexDriver } from './createConvexDriver.js';
import { createRoutedDriver } from './createRoutedDriver.js';
import { normalizeStorageSubscriptionPaths } from './subscriptionManifests.js';
import { FEATURE_FLAGS, collectionRouting } from './featureFlags.js';
import { seedLowChurn } from './migrations/seedLowChurn.js';
import { seedChars } from './migrations/seedChars.js';
import { seedCombatJournalAudit, resetConvexAuditAndSnapshots } from './migrations/seedCombatJournalAudit.js';
import { seedRemaining } from './migrations/seedRemaining.js';
import { loadCampaignSandboxState } from './campaignSandboxStorage.js';
import {
  RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
  STORAGE_MODE_LOCAL_SANDBOX,
} from '../campaignSandboxContract.js';

/**
 * @param {{
 *   defaults: Object,
 *   demoData?: Object,
 *   viewerContext?: { viewerKind: 'dm'|'player', viewerCharId?: string|null } | null,
 *   viewerAuthToken?: string | null,
 *   useServerDerivedViewerQueries?: boolean,
 *   forceLocalMode?: boolean,
 *   runtimeScope?: string | null,
 *   storageMode?: string | null,
 *   sandboxId?: string | null,
 *   sourceCampaignId?: string | null,
 *   sourceMode?: string | null,
 *   subscriptionPaths?: string[],
 *   subscriptionArgs?: Record<string, any>,
 * }} [config]
 *
 * Session 7B: `viewerContext` routes chars/systems subscriptions through
 * projected queries. When null/undefined, driver behaves identically to
 * Sessions 1-6 (non-projected `chars:list` / `systems:list` subscriptions).
 * The driver is constructed ONCE at mount — a changed viewerContext across
 * renders does NOT re-create the driver. DevTools helpers that change the
 * viewer context trigger `location.reload()` to force re-mount.
 */
export const useStorage = (config = { defaults: {}, demoData: {} }) => {
  const {
    viewerContext = null,
    viewerAuthToken = null,
    useServerDerivedViewerQueries = false,
    forceLocalMode = false,
    subscriptionPaths = null,
    subscriptionArgs = {},
  } = config;
  const isCampaignSandboxStorageContext = (
    config?.runtimeScope === RUNTIME_SCOPE_CAMPAIGN_SANDBOX
    || config?.storageMode === STORAGE_MODE_LOCAL_SANDBOX
  );
  const effectiveViewerAuthToken = isCampaignSandboxStorageContext ? null : viewerAuthToken;
  const driverConfig = (isCampaignSandboxStorageContext && viewerAuthToken)
    ? { ...config, viewerAuthToken: null }
    : config;

  // Initial state must match what the selected driver will emit synchronously on
  // first subscribe. buildInitialData is what BOTH drivers derive from — so the
  // React state seed and the driver's first emit are identical.
  const initialDataRef = useRef(null);
  if (!initialDataRef.current) {
    const initialData = buildInitialData(driverConfig);
    initialDataRef.current = loadCampaignSandboxState(driverConfig, initialData);
  }

  // Driver is created exactly once per component-instance lifetime via the
  // ref + lazy-init pattern. Why not `useState(() => pickDriver())`? Because
  // React 18 StrictMode dev double-invokes useState's initializer — the second
  // call's return is discarded by React, but its SIDE EFFECTS (listener
  // registration on the Convex websocket) aren't undone, leaking a driver
  // instance whose listeners we never dispose. The ref pattern guards
  // construction explicitly: on the second render-body-invoke,
  // `driverRef.current` is already set, so we skip creating a second driver.
  // On true unmount + remount, useEffect cleanup disposes the ref'd driver
  // and the fresh mount creates a new one.
  const driverRef = useRef(null);
  if (driverRef.current === null) {
    driverRef.current = (() => {
      if (forceLocalMode || isCampaignSandboxStorageContext || shouldForceLocalMode()) {
        console.log(forceLocalMode || isCampaignSandboxStorageContext
          ? 'Forced local mode (via launch/storage context). Cloud backend skipped.'
          : 'Forced local mode (via ?local=1). Cloud backend skipped.');
        return createLocalDriver(driverConfig);
      }

      // Session 10 (2026-04-20): Firebase deleted. Routed path now composes
      // Convex + Local only; single-driver path returns Convex with Local
      // fallback. Session 7B's viewerContext plumbing lives on the Convex
      // driver construction — null viewerContext yields non-projected
      // queries (Sessions 1-6 behavior).
      if (FEATURE_FLAGS.useRoutedDriver) {
        const convex = createConvexDriver({
          viewerContext,
          viewerAuthToken: effectiveViewerAuthToken,
          useServerDerivedViewerQueries,
        });
        const local = createLocalDriver(driverConfig);
        if (!convex) {
          console.log('Routed driver requested but Convex unavailable. Falling back to local.');
          return local;
        }
        return createRoutedDriver({
          routing: collectionRouting,
          drivers: {
            convex,
            local,
          },
          compositeDefault: 'convex',
        });
      }

      const convexDriver = createConvexDriver({
        viewerContext,
        viewerAuthToken: effectiveViewerAuthToken,
        useServerDerivedViewerQueries,
      });
      return convexDriver || createLocalDriver(driverConfig);
    })();
  }
  const driver = driverRef.current;

  useEffect(() => {
    if (!driver || typeof driver.setViewerAuthToken !== 'function') return;
    driver.setViewerAuthToken(effectiveViewerAuthToken);
  }, [driver, effectiveViewerAuthToken]);

  const [data, setData] = useState(initialDataRef.current);
  const [connectionStatus, setConnectionStatus] = useState(() => driver.getConnectionStatus());
  const activeSubscriptionPaths = useMemo(
    () => normalizeStorageSubscriptionPaths(subscriptionPaths),
    [subscriptionPaths]
  );
  const activeSubscriptionPathKey = activeSubscriptionPaths.join('\u0000');
  const subscriptionArgsKey = useMemo(() => {
    try { return JSON.stringify(subscriptionArgs || {}); }
    catch { return String(Date.now()); }
  }, [subscriptionArgs]);
  const subscriptionConfigRef = useRef({ activeSubscriptionPaths, subscriptionArgs });
  subscriptionConfigRef.current = { activeSubscriptionPaths, subscriptionArgs };
  const subscriptionUnsubsRef = useRef(new Map());
  const driverDisposeTimerRef = useRef(null);
  const driverDisposeGenerationRef = useRef(0);

  // Subscribe only to the active manifest paths. Each handler updates one slice
  // of React state, triggering re-renders only in consumers that use that slice.
  useEffect(() => {
    const { activeSubscriptionPaths: paths, subscriptionArgs: args } = subscriptionConfigRef.current;
    if (driver && typeof driver.setSubscriptionArgs === 'function') {
      paths.forEach(path => {
        if (!Object.prototype.hasOwnProperty.call(args || {}, path)) return;
        driver.setSubscriptionArgs(path, args[path] || null);
      });
    }
    const activeSet = new Set(paths);
    subscriptionUnsubsRef.current.forEach((unsubscribe, path) => {
      if (activeSet.has(path)) return;
      try { unsubscribe(); } catch (e) { console.error(`useStorage unsubscribe error on ${path}:`, e); }
      subscriptionUnsubsRef.current.delete(path);
    });
    setData(d => {
      let next = d;
      for (const [path, defaultValue] of Object.entries(initialDataRef.current || {})) {
        if (activeSet.has(path) || d[path] === defaultValue) continue;
        if (next === d) next = { ...d };
        next[path] = defaultValue;
      }
      return next;
    });
    paths.forEach(path => {
      if (subscriptionUnsubsRef.current.has(path)) return;
      const unsubscribe = driver.subscribe(path, value => {
        setData(d => (d[path] === value ? d : { ...d, [path]: value }));
      });
      subscriptionUnsubsRef.current.set(path, unsubscribe);
    });
  }, [driver, activeSubscriptionPathKey, subscriptionArgsKey]);

  useEffect(() => () => {
    subscriptionUnsubsRef.current.forEach((unsubscribe, path) => {
      try { unsubscribe(); } catch (e) { console.error(`useStorage unsubscribe error on ${path}:`, e); }
    });
    subscriptionUnsubsRef.current.clear();
  }, []);

  useEffect(() => {
    if (driverDisposeTimerRef.current !== null) {
      clearTimeout(driverDisposeTimerRef.current);
      driverDisposeTimerRef.current = null;
    }
    const generation = driverDisposeGenerationRef.current + 1;
    driverDisposeGenerationRef.current = generation;
    return () => {
      const driverToDispose = driverRef.current;
      if (!driverToDispose || typeof driverToDispose.dispose !== 'function') return;
      if (driverDisposeTimerRef.current !== null) {
        clearTimeout(driverDisposeTimerRef.current);
      }
      driverDisposeTimerRef.current = setTimeout(() => {
        if (driverDisposeGenerationRef.current !== generation) return;
        driverDisposeTimerRef.current = null;
        try {
          if (typeof driverToDispose.flushPending === 'function') driverToDispose.flushPending();
        } catch (e) { console.error('useStorage driver dispose error:', e); }
        try { driverToDispose.dispose(); } catch (e) { console.error('useStorage driver dispose error:', e); }
      }, 0);
    };
  }, []);

  // Poll connectionStatus. Drivers update it in-place; we lift to React state for
  // re-rendering. 500ms is a good balance — 2Hz is below perceivable latency for
  // a status indicator and well below the rate of status changes.
  useEffect(() => {
    const tick = () => setConnectionStatus(prev => {
      const next = driver.getConnectionStatus();
      return next === prev ? prev : next;
    });
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [driver]);

  // Historical Convex migration helpers. These are no longer ambient DevTools
  // seed/reset globals; install the dangerous namespace explicitly when a beta
  // recovery playbook needs it. Ref tracks latest data for call time.
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (typeof window === 'undefined') return;
    if (forceLocalMode || isCampaignSandboxStorageContext || shouldForceLocalMode()) return;
    const INSTALL_CONVEX_MIGRATION_HELPERS_CONFIRMATION = 'INSTALL_CONVEX_MIGRATION_HELPERS';
    const CONVEX_MIGRATION_HELPER_CONFIRMATIONS = Object.freeze({
      seedLowChurn: 'SEED_CONVEX_LOW_CHURN_FROM_CURRENT_STATE',
      seedChars: 'SEED_CONVEX_CHARS_FROM_CURRENT_STATE',
      seedCombatJournalAudit: 'SEED_CONVEX_COMBAT_JOURNAL_AUDIT_FROM_CURRENT_STATE',
      seedRemaining: 'SEED_CONVEX_REMAINING_FROM_CURRENT_STATE',
      resetAuditAndSnapshots: 'RESET_CONVEX_AUDIT_AND_SNAPSHOTS',
    });
    const requireUrl = () => {
      const url = process.env.REACT_APP_CONVEX_URL;
      if (!url) {
        console.error('REACT_APP_CONVEX_URL not set. Run `npx convex dev` to create a deployment.');
        return null;
      }
      return url;
    };
    const getStoredConvexAuthToken = () => {
      const rawTokenKey = Object.keys(window.localStorage).find(k => /__convexAuthJWT/.test(k));
      if (!rawTokenKey) return null;
      return window.localStorage.getItem(rawTokenKey) || null;
    };
    const requireMigrationConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this historical Convex migration helper.`);
    };
    const seedAuthOptions = () => ({ authToken: getStoredConvexAuthToken() });
    window.__installConvexMigrationHelpers__ = ({ confirm } = {}) => {
      requireMigrationConfirmation(
        confirm,
        INSTALL_CONVEX_MIGRATION_HELPERS_CONFIRMATION,
        '__installConvexMigrationHelpers__'
      );
      const helpers = {
        confirmations: CONVEX_MIGRATION_HELPER_CONFIRMATIONS,
        seedLowChurn: async ({ confirm: helperConfirm } = {}) => {
          requireMigrationConfirmation(helperConfirm, CONVEX_MIGRATION_HELPER_CONFIRMATIONS.seedLowChurn, 'seedLowChurn');
          const url = requireUrl();
          if (!url) return null;
          console.log('Seeding Convex low-churn tables from current in-memory state...');
          return await seedLowChurn(dataRef.current, url, seedAuthOptions());
        },
        seedChars: async ({ confirm: helperConfirm } = {}) => {
          requireMigrationConfirmation(helperConfirm, CONVEX_MIGRATION_HELPER_CONFIRMATIONS.seedChars, 'seedChars');
          const url = requireUrl();
          if (!url) return null;
          console.log('Seeding Convex chars + systems tables from current in-memory state...');
          return await seedChars(dataRef.current, url, seedAuthOptions());
        },
        seedCombatJournalAudit: async ({ confirm: helperConfirm } = {}) => {
          requireMigrationConfirmation(helperConfirm, CONVEX_MIGRATION_HELPER_CONFIRMATIONS.seedCombatJournalAudit, 'seedCombatJournalAudit');
          const url = requireUrl();
          if (!url) return null;
          console.log('Seeding Convex combat + journal rows + audit from current in-memory state...');
          return await seedCombatJournalAudit(dataRef.current, url, seedAuthOptions());
        },
        seedRemaining: async ({ confirm: helperConfirm } = {}) => {
          requireMigrationConfirmation(helperConfirm, CONVEX_MIGRATION_HELPER_CONFIRMATIONS.seedRemaining, 'seedRemaining');
          const url = requireUrl();
          if (!url) return null;
          console.log('Seeding Convex messages + groupTravelLog + food singletons from current in-memory state...');
          return await seedRemaining(dataRef.current, url, seedAuthOptions());
        },
        resetAuditAndSnapshots: async ({ confirm: helperConfirm } = {}) => {
          requireMigrationConfirmation(helperConfirm, CONVEX_MIGRATION_HELPER_CONFIRMATIONS.resetAuditAndSnapshots, 'resetAuditAndSnapshots');
          const url = requireUrl();
          if (!url) return null;
          console.log('Resetting Convex audit + combatSnapshots to empty defaults (ignores in-memory state)...');
          return await resetConvexAuditAndSnapshots(url, seedAuthOptions());
        },
      };
      window.__convexMigrationHelpers__ = helpers;
      console.warn('Installed historical Convex migration helpers under window.__convexMigrationHelpers__. Use only from an explicit beta recovery playbook.');
      return helpers;
    };
    // Session 8/6.2 auth diagnostics. Auth flag helpers live at the root so
    // they are available before App/useStorage mounts; storage keeps only
    // state-backed diagnostics that need the current storage data/auth token.
    //
    // A client that can actually CALL authed functions requires the auth
    // token to be threaded into the driver's ConvexClient via setAuth() —
    // deferred to Session 7B. For Session 8 we call these mutations via a
    // fresh short-lived ConvexClient that reads the token from localStorage
    // (Convex Auth persists tokens there). See claimAsDM helper below.
    const getAuthedConvexClient = async () => {
      const url = process.env.REACT_APP_CONVEX_URL;
      if (!url) throw new Error('REACT_APP_CONVEX_URL not set');
      const { ConvexHttpClient } = await import('convex/browser');
      const httpClient = new ConvexHttpClient(url);
      const token = getStoredConvexAuthToken();
      if (!token) throw new Error('Auth token missing.');
      httpClient.setAuth(token);
      return httpClient;
    };

    const INSTALL_STORAGE_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_STORAGE_DEBUG_HELPERS';
    const STORAGE_DEBUG_CONFIRMATIONS = Object.freeze({
      dumpRawAudit: 'DUMP_RAW_AUDIT_FOR_DEV',
      dumpRawChar: 'DUMP_RAW_CHAR_FOR_DEV',
      selectStandaloneOwnedChar: 'SELECT_STANDALONE_OWNED_CHAR_FOR_DEV',
    });
    const requireStorageDebugConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this storage diagnostic.`);
    };
    const auditSummary = (audit = {}) => ({
      activeSessionId: audit?.activeSessionId ?? null,
      sessionCount: Object.keys(audit?.sessions || {}).length,
      eventCount: Object.keys(audit?.events || {}).length,
      snapshotCount: Object.keys(audit?.snapshots || {}).length,
    });
    const charDebugSummary = (charId, ch) => {
      if (!ch) return null;
      const sysList = Array.isArray(ch.systemList) ? ch.systemList : [];
      return {
        charId,
        name: ch?.name || '(unnamed)',
        race: ch?.race || '',
        level: ch?.level || '',
        scopeKind: ch?.scopeKind || 'standalone',
        campaignStatus: ch?.campaignStatus || null,
        systemCount: sysList.length,
        systems: sysList.map((inst) => ({
          systemId: inst?.systemId || inst?.id || null,
          keyCount: Object.keys(inst || {}).length,
          hasDmNotes: 'dmNotes' in (inst || {}),
          hasApplyHiddenEffects: 'applyHiddenEffects' in (inst || {}),
          hiddenEffectsCount: inst?._hiddenEffectsCount ?? null,
        })),
      };
    };
    window.__installStorageDebugHelpers__ = ({ confirm } = {}) => {
      requireStorageDebugConfirmation(
        confirm,
        INSTALL_STORAGE_DEBUG_HELPERS_CONFIRMATION,
        '__installStorageDebugHelpers__'
      );
      const helpers = {
        confirmations: STORAGE_DEBUG_CONFIRMATIONS,
        whoAmI: async () => {
          const c = await getAuthedConvexClient();
          const me = await c.query('authHelpers:whoAmI', {});
          console.log('whoAmI:', me);
          return me;
        },
        listChars: ({ raw = false, confirm: helperConfirm, rawConfirm } = {}) => {
          const hasRawConfirmation = (helperConfirm || rawConfirm) === STORAGE_DEBUG_CONFIRMATIONS.dumpRawChar;
          if (raw) {
            requireStorageDebugConfirmation(
              helperConfirm || rawConfirm,
              STORAGE_DEBUG_CONFIRMATIONS.dumpRawChar,
              'listChars'
            );
          }
          const c = dataRef.current?.chars || {};
          const summary = Object.entries(c).map(([charId, ch]) => {
            const redacted = {
              charId,
              name: ch?.name || '(unnamed)',
              race: ch?.race || '',
              level: ch?.level || '',
              scopeKind: ch?.scopeKind || 'standalone',
              campaignStatus: ch?.campaignStatus || null,
              systemCount: Array.isArray(ch?.systemList) ? ch.systemList.length : 0,
            };
            if (hasRawConfirmation) {
              return {
                ...redacted,
                ownerUserId: ch?.ownerUserId || null,
                dmUserId: ch?.dmUserId || null,
              };
            }
            return redacted;
          });
          console.table(summary);
          return summary;
        },
        dumpAudit: ({ raw = false, confirm: helperConfirm, rawConfirm } = {}) => {
          const audit = dataRef.current?.audit;
          const hasRawConfirmation = (helperConfirm || rawConfirm) === STORAGE_DEBUG_CONFIRMATIONS.dumpRawAudit;
          if (raw) {
            requireStorageDebugConfirmation(
              helperConfirm || rawConfirm,
              STORAGE_DEBUG_CONFIRMATIONS.dumpRawAudit,
              'dumpAudit'
            );
          }
          if (hasRawConfirmation) {
            console.log('--- raw audit state (as app sees it) ---');
            console.log(audit);
            return audit;
          }
          const summary = auditSummary(audit);
          console.log('--- audit summary (as app sees it) ---');
          console.table([summary]);
          return summary;
        },
        dumpChar: (charId, { raw = false, confirm: helperConfirm, rawConfirm } = {}) => {
          const c = dataRef.current?.chars?.[charId];
          if (!c) {
            console.warn(`No char "${charId}" in current app state. Run window.__storageDebugHelpers__.listChars() to see available charIds.`);
            return null;
          }
          const hasRawConfirmation = (helperConfirm || rawConfirm) === STORAGE_DEBUG_CONFIRMATIONS.dumpRawChar;
          if (raw) {
            requireStorageDebugConfirmation(
              helperConfirm || rawConfirm,
              STORAGE_DEBUG_CONFIRMATIONS.dumpRawChar,
              'dumpChar'
            );
          }
          if (hasRawConfirmation) {
            console.log(`--- raw char "${charId}" as app sees it ---`);
            console.log(c);
            return c;
          }
          const summary = charDebugSummary(charId, c);
          console.log(`--- char "${charId}" summary as app sees it ---`);
          console.log(summary);
          return summary;
        },
        selectStandaloneOwnedChar: async (charId, { confirm: helperConfirm } = {}) => {
          requireStorageDebugConfirmation(
            helperConfirm,
            STORAGE_DEBUG_CONFIRMATIONS.selectStandaloneOwnedChar,
            'selectStandaloneOwnedChar'
          );
          const c = await getAuthedConvexClient();
          const r = await c.mutation('authHelpers:claimCharAsOwner', { charId });
          console.log(`selectStandaloneOwnedChar(${charId}) ->`, r);
          return r;
        },
      };
      window.__storageDebugHelpers__ = helpers;
      console.warn('Installed storage diagnostics under window.__storageDebugHelpers__. Raw dumps and mutating selectors require per-call confirmation.');
      return helpers;
    };
    delete window.__dumpAudit__;

    const INSTALL_PROJECTION_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_PROJECTION_DEBUG_HELPERS';
    const requireProjectionDebugConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to install projection diagnostics.`);
    };
    const parseProjectedCharRow = (row) => {
      try { return row?.data ? JSON.parse(row.data) : null; }
      catch { return null; }
    };
    window.__installProjectionDebugHelpers__ = ({ confirm } = {}) => {
      requireProjectionDebugConfirmation(
        confirm,
        INSTALL_PROJECTION_DEBUG_HELPERS_CONFIRMATION,
        '__installProjectionDebugHelpers__'
      );
      const helpers = {
        inspectSystems: async (charId) => {
          if (!charId || typeof charId !== 'string') {
            throw new Error('inspectSystems: charId is required.');
          }
          const c = await getAuthedConvexClient();
          const rows = await c.query('chars:listForAuthedViewer', {});
          const row = (rows || []).find(r => r?.charId === charId);
          if (!row) {
            console.warn(`inspectSystems: char "${charId}" is not visible to the authenticated viewer.`);
            return [];
          }
          const charData = parseProjectedCharRow(row);
          const systems = Array.isArray(charData?.systemList) ? charData.systemList : [];
          console.log(`--- projected systems for authenticated viewer charId=${charId} ---`);
          console.log('count:', systems.length);
          systems.forEach((inst, i) => {
            console.log(`[${i}] systemId=${inst?.systemId || '?'}`);
            console.log('    instance keys:', Object.keys(inst || {}).sort());
            console.log('    has dmNotes:', 'dmNotes' in (inst || {}));
            console.log('    has applyHiddenEffects:', 'applyHiddenEffects' in (inst || {}));
            console.log('    _hiddenEffectsCount:', inst?._hiddenEffectsCount);
          });
          return systems;
        },
        listProjectedChars: async () => {
          const c = await getAuthedConvexClient();
          const rows = await c.query('chars:listForAuthedViewer', {});
          const summary = (rows || []).map(row => {
            const charData = parseProjectedCharRow(row);
            return {
              charId: row?.charId,
              name: charData?.name || '(unnamed)',
              systemCount: Array.isArray(charData?.systemList) ? charData.systemList.length : 0,
            };
          });
          console.table(summary);
          return summary;
        },
      };
      window.__projectionDebugHelpers__ = helpers;
      console.warn('Installed projection diagnostics under window.__projectionDebugHelpers__. These use authenticated viewer context only.');
      return helpers;
    };
    delete window.__inspectProjectedSystems__;

    delete window.__convexAuthWhoAmI__;
    delete window.__selectStandaloneOwnedChar__;
    delete window.__claimCharAsOwner__;

    // Session 7B (2026-04-20): viewer-context testing helpers. These exist
    // so developers who are DMs of every char by default can still exercise
    // the player-view render path without claiming chars as player-owners.
    //
    // Usage:
    //   const h = window.__installStorageDebugHelpers__({ confirm: 'INSTALL_STORAGE_DEBUG_HELPERS' })
    //   h.listChars()                         → inspect redacted available chars
    //   h.dumpChar('demo-lika')               → inspect redacted current projected state
    // Root-level auth debug helpers own __devSetTestViewerContext__ and
    // __devClearTestViewerContext__ so they are available before App mounts.
    //
    // The test context lives in sessionStorage so it doesn't survive tab
    // close (intentional — prevents stale override in fresh sessions).
    delete window.__listChars__;
    delete window.__dumpChar__;

    // Bug-report diagnostic helpers. Historical implementations stay local to
    // this effect and are exposed only through the explicit installer below.
    // are dev-only — Session 2 ships the real Quick Report overlay UI; until
    // then, these are how we exercise the backend round-trip.
    //
    // Usage examples:
    //   const h = window.__installBugReportDebugHelpers__({ confirm: 'INSTALL_BUG_REPORT_DEBUG_HELPERS' })
    //   await h.submitTestBugReport({}, { confirm: 'SUBMIT_TEST_BUG_REPORT' })
    //   await h.listBugReports({ mine: true })
    //   await h.leaderboard()
    //   await h.unreadCount()
    //   await h.markTestExported(Date.now(), { confirm: 'MARK_BUG_REPORT_TEST_EXPORTED' })
    const submitTestBugReport = async (overrides = {}) => {
      const c = await getAuthedConvexClient();
      const reportId = `rpt-test-${Date.now().toString(36)}`;
      const data = {
        title: overrides.title || 'Test report from DevTools',
        description: overrides.description || 'Verifying Session 1 round-trip — schema, handlers, hook.',
        tags: overrides.tags || ['bug', 'ui'],
        importance: overrides.importance ?? 5,
        elements: [],
        lastAuditEvents: [],
        breadcrumbs: [],
        characterSnapshot: null,
        appContext: {
          commitSha: 'dev',
          viewport: { w: window.innerWidth, h: window.innerHeight },
          userAgent: navigator.userAgent,
          view: 'devtools',
          isDM: undefined,
          charId: null,
          route: window.location.pathname + window.location.search,
        },
        statusHistory: [],
      };
      const r = await c.mutation('bugReports:submit', {
        reportId,
        data: JSON.stringify(data),
        screenshotStorageIds: [],
      });
      console.log(`__submitTestBugReport__ -> ${r}`);
      return r;
    };

    const listBugReports = async ({ mine = false, status = null, numItems = 25 } = {}) => {
      const c = await getAuthedConvexClient();
      const args = { paginationOpts: { numItems, cursor: null } };
      if (mine) args.mine = true;
      if (status) args.status = status;
      const page = await c.query('bugReports:list', args);
      console.log(`__listBugReports__ -> ${page.page.length} of ${page.isDone ? 'all' : 'more'}`);
      console.table(page.page.map(r => ({
        reportId: r.reportId,
        status: r.status,
        createdAt: new Date(r.createdAt).toISOString(),
        lastActivityAt: new Date(r.lastActivityAt).toISOString(),
        hasUnreadForReporter: r.hasUnreadForReporter,
        hasUnreadForDm: r.hasUnreadForDm,
      })));
      return page;
    };

    const bugReportLeaderboard = async (limit = 10) => {
      const c = await getAuthedConvexClient();
      const rows = await c.query('bugReports:leaderboard', { limit });
      console.table(rows);
      return rows;
    };

    const bugReportUnreadCount = async () => {
      const c = await getAuthedConvexClient();
      const n = await c.query('bugReports:myUnreadCount', {});
      console.log(`__bugReportUnreadCount__ -> ${n}`);
      return n;
    };

    const getBugReport = async (reportId) => {
      const c = await getAuthedConvexClient();
      const r = await c.query('bugReports:getById', { reportId });
      console.log(`__getBugReport__(${reportId}) ->`, r);
      return r;
    };

    const markBugReportTestExported = async (ts = Date.now()) => {
      const c = await getAuthedConvexClient();
      await c.mutation('bugReports:markBulkExported', { ts });
      console.log(`__markBugReportTestExported__ -> set lastBulkExportAt = ${ts} (${new Date(ts).toISOString()})`);
      return ts;
    };

    const listBugReportsSinceLastExport = async ({ numItems = 50 } = {}) => {
      const c = await getAuthedConvexClient();
      const since = await c.query('settings:getLastBulkExportAt', {});
      console.log(`__listBugReportsSinceLastExport__: bookmark = ${since} (${since ? new Date(since).toISOString() : 'never'})`);
      const page = await c.query('bugReports:listSinceLastExport', {
        paginationOpts: { numItems, cursor: null },
        since,
      });
      console.log(`__listBugReportsSinceLastExport__ -> ${page.page.length} reports`);
      console.table(page.page.map(r => ({
        reportId: r.reportId,
        status: r.status,
        lastActivityAt: new Date(r.lastActivityAt).toISOString(),
      })));
      return page;
    };

    const legacyBugReportDebugHelpers = {
      submitTestBugReport,
      listBugReports,
      leaderboard: bugReportLeaderboard,
      unreadCount: bugReportUnreadCount,
      getBugReport,
      markTestExported: markBugReportTestExported,
      listSinceLastExport: listBugReportsSinceLastExport,
    };
    const INSTALL_BUG_REPORT_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_BUG_REPORT_DEBUG_HELPERS';
    const BUG_REPORT_DEBUG_CONFIRMATIONS = Object.freeze({
      submitTestBugReport: 'SUBMIT_TEST_BUG_REPORT',
      markTestExported: 'MARK_BUG_REPORT_TEST_EXPORTED',
    });
    const requireBugReportDebugConfirmation = (received, expected, helperName) => {
      if (received === expected) return;
      throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this bug-report diagnostic helper.`);
    };
    window.__installBugReportDebugHelpers__ = ({ confirm } = {}) => {
      requireBugReportDebugConfirmation(
        confirm,
        INSTALL_BUG_REPORT_DEBUG_HELPERS_CONFIRMATION,
        '__installBugReportDebugHelpers__'
      );
      const helpers = {
        confirmations: BUG_REPORT_DEBUG_CONFIRMATIONS,
        submitTestBugReport: async (overrides = {}, { confirm: helperConfirm } = {}) => {
          requireBugReportDebugConfirmation(helperConfirm, BUG_REPORT_DEBUG_CONFIRMATIONS.submitTestBugReport, 'submitTestBugReport');
          return legacyBugReportDebugHelpers.submitTestBugReport(overrides);
        },
        listBugReports: legacyBugReportDebugHelpers.listBugReports,
        leaderboard: legacyBugReportDebugHelpers.leaderboard,
        unreadCount: legacyBugReportDebugHelpers.unreadCount,
        getBugReport: legacyBugReportDebugHelpers.getBugReport,
        markTestExported: async (ts = Date.now(), { confirm: helperConfirm } = {}) => {
          requireBugReportDebugConfirmation(helperConfirm, BUG_REPORT_DEBUG_CONFIRMATIONS.markTestExported, 'markTestExported');
          return legacyBugReportDebugHelpers.markTestExported(ts);
        },
        listSinceLastExport: legacyBugReportDebugHelpers.listSinceLastExport,
      };
      window.__bugReportDebugHelpers__ = helpers;
      console.warn('Installed bug-report diagnostic helpers under window.__bugReportDebugHelpers__. Use only from an explicit beta support playbook.');
      return helpers;
    };
    delete window.__submitTestBugReport__;
    delete window.__listBugReports__;
    delete window.__bugReportLeaderboard__;
    delete window.__bugReportUnreadCount__;
    delete window.__getBugReport__;
    delete window.__markBugReportTestExported__;
    delete window.__listBugReportsSinceLastExport__;

    return () => {
      try {
        delete window.__seedConvexLowChurn__;
        delete window.__seedConvexChars__;
        delete window.__seedConvexCombat__;
        delete window.__seedConvexRemaining__;
        delete window.__resetConvexAuditAndSnapshots__;
        delete window.__installConvexMigrationHelpers__;
        delete window.__convexMigrationHelpers__;
        delete window.__installStorageDebugHelpers__;
        delete window.__storageDebugHelpers__;
        delete window.__dumpAudit__;
        delete window.__installProjectionDebugHelpers__;
        delete window.__projectionDebugHelpers__;
        delete window.__inspectProjectedSystems__;
        delete window.__convexAuthWhoAmI__;
        delete window.__selectStandaloneOwnedChar__;
        delete window.__claimCharAsOwner__;
        delete window.__listChars__;
        delete window.__dumpChar__;
        delete window.__setTestViewerContext__;
        delete window.__clearTestViewerContext__;
        delete window.__installBugReportDebugHelpers__;
        delete window.__bugReportDebugHelpers__;
        delete window.__submitTestBugReport__;
        delete window.__listBugReports__;
        delete window.__bugReportLeaderboard__;
        delete window.__bugReportUnreadCount__;
        delete window.__getBugReport__;
        delete window.__markBugReportTestExported__;
        delete window.__listBugReportsSinceLastExport__;
      } catch (_) { /* noop */ }
    };
  }, [forceLocalMode, isCampaignSandboxStorageContext]);

  // Session 6.2: defer driver disposal by one macrotask. React StrictMode can
  // replay effects as cleanup -> setup on the same component instance; the
  // setup above cancels that pending disposal. On a true unmount or keyed App
  // remount, no replay setup runs for the old instance, so Convex/local/routed
  // drivers close after subscription-specific cleanup has completed.

  // --- Save methods ---
  // One per collection, all delegating to driver.save(path, value). `useCallback`
  // keeps references stable across renders so consumers' dependency arrays stay
  // quiet. Each is its own callback rather than a shared factory so DevTools
  // shows meaningful names.

  const saveChars = useCallback(v => driver.save('chars', v), [driver]);
  const saveFood = useCallback(v => driver.save('food', v), [driver]);
  const saveDiets = useCallback(v => driver.save('diets', v), [driver]);
  const saveDiseases = useCallback(v => driver.save('diseases', v), [driver]);
  const saveSystems = useCallback((v, options) => driver.save('systems', v, options), [driver]);
  const saveFoodTypes = useCallback(v => driver.save('foodTypes', v), [driver]);
  const saveLocations = useCallback(v => driver.save('locations', v), [driver]);
  const saveGlobalSettings = useCallback(v => driver.save('globalSettings', v), [driver]);
  const saveSpellLibrary = useCallback(v => driver.save('spellLibrary', v), [driver]);
  const saveGroupTravelLog = useCallback(v => driver.save('groupTravelLog', v), [driver]);
  const saveCurrentLocation = useCallback(v => driver.save('currentLocation', v), [driver]);
  const saveMessages = useCallback((v, options) => driver.save('messages', v, options), [driver]);
  const saveJournal = useCallback((v, options) => driver.save('journal', v, options), [driver]);
  const saveDmJournal = useCallback((v, options) => driver.save('dmJournal', v, options), [driver]);
  const saveNpcLibrary = useCallback(v => driver.save('npcLibrary', v), [driver]);
  const saveJournalSettings = useCallback(v => driver.save('journalSettings', v), [driver]);
  const saveCombat = useCallback((v, options) => driver.save('combat', v, options), [driver]);
  const saveCombatSnapshots = useCallback(v => driver.save('combatSnapshots', v), [driver]);
  // 2026-04-23: party shared inventory.
  const savePartyInventory = useCallback(v => driver.save('partyInventory', v), [driver]);

  const upsertJournalEntry = useMemo(() => (
    typeof driver.upsertJournalEntry === 'function'
      ? (args) => driver.upsertJournalEntry(args)
      : null
  ), [driver]);
  const deleteJournalEntry = useMemo(() => (
    typeof driver.deleteJournalEntry === 'function'
      ? (args) => driver.deleteJournalEntry(args)
      : null
  ), [driver]);
  const setJournalEntryCampaignScope = useMemo(() => (
    typeof driver.setJournalEntryCampaignScope === 'function'
      ? (args) => driver.setJournalEntryCampaignScope(args)
      : null
  ), [driver]);
  const upsertDmJournalEntry = useMemo(() => (
    typeof driver.upsertDmJournalEntry === 'function'
      ? (args) => driver.upsertDmJournalEntry(args)
      : null
  ), [driver]);
  const deleteDmJournalEntry = useMemo(() => (
    typeof driver.deleteDmJournalEntry === 'function'
      ? (args) => driver.deleteDmJournalEntry(args)
      : null
  ), [driver]);
  const upsertGlobalLoreEntry = useMemo(() => (
    typeof driver.upsertGlobalLoreEntry === 'function'
      ? (args) => driver.upsertGlobalLoreEntry(args)
      : null
  ), [driver]);
  const deleteGlobalLoreEntry = useMemo(() => (
    typeof driver.deleteGlobalLoreEntry === 'function'
      ? (args) => driver.deleteGlobalLoreEntry(args)
      : null
  ), [driver]);

  const lookupJournalEntriesByName = useMemo(() => (
    typeof driver.lookupJournalEntriesByName === 'function'
      ? (args) => driver.lookupJournalEntriesByName(args)
      : null
  ), [driver]);

  const lookupDmJournalEntriesByName = useMemo(() => (
    typeof driver.lookupDmJournalEntriesByName === 'function'
      ? (args) => driver.lookupDmJournalEntriesByName(args)
      : null
  ), [driver]);

  const discoverJournalEntries = useMemo(() => (
    typeof driver.discoverJournalEntries === 'function'
      ? (args) => driver.discoverJournalEntries(args)
      : null
  ), [driver]);

  const discoverDmJournalEntries = useMemo(() => (
    typeof driver.discoverDmJournalEntries === 'function'
      ? (args) => driver.discoverDmJournalEntries(args)
      : null
  ), [driver]);
  const markStatusMessageSeen = useMemo(() => (
    typeof driver.markStatusMessageSeen === 'function'
      ? (args) => driver.markStatusMessageSeen(args)
      : null
  ), [driver]);
  const dismissStatusMessage = useMemo(() => (
    typeof driver.dismissStatusMessage === 'function'
      ? (args) => driver.dismissStatusMessage(args)
      : null
  ), [driver]);
  const markDmChatRead = useMemo(() => (
    typeof driver.markDmChatRead === 'function'
      ? (args) => driver.markDmChatRead(args)
      : null
  ), [driver]);
  const sendStatusMessage = useMemo(() => (
    typeof driver.sendStatusMessage === 'function'
      ? (args) => driver.sendStatusMessage(args)
      : null
  ), [driver]);
  const sendDmChatMessage = useMemo(() => (
    typeof driver.sendDmChatMessage === 'function'
      ? (args) => driver.sendDmChatMessage(args)
      : null
  ), [driver]);
  const deleteDmChatMessage = useMemo(() => (
    typeof driver.deleteDmChatMessage === 'function'
      ? (args) => driver.deleteDmChatMessage(args)
      : null
  ), [driver]);

  const patchCharacterVitals = useMemo(() => (
    typeof driver.patchCharacterVitals === 'function'
      ? (args) => driver.patchCharacterVitals(args)
      : null
  ), [driver]);
  const patchCharacterSystemInstance = useMemo(() => (
    typeof driver.patchCharacterSystemInstance === 'function'
      ? (args) => driver.patchCharacterSystemInstance(args)
      : null
  ), [driver]);
  const patchCharacterSystemState = useMemo(() => (
    typeof driver.patchCharacterSystemState === 'function'
      ? (args) => driver.patchCharacterSystemState(args)
      : null
  ), [driver]);
  const patchCharacterSystemStateAndVitals = useMemo(() => (
    typeof driver.patchCharacterSystemStateAndVitals === 'function'
      ? (args) => driver.patchCharacterSystemStateAndVitals(args)
      : null
  ), [driver]);
  const patchCharacterSystemStateAndResources = useMemo(() => (
    typeof driver.patchCharacterSystemStateAndResources === 'function'
      ? (args) => driver.patchCharacterSystemStateAndResources(args)
      : null
  ), [driver]);
  const patchCharacterSystemStateAndEffects = useMemo(() => (
    typeof driver.patchCharacterSystemStateAndEffects === 'function'
      ? (args) => driver.patchCharacterSystemStateAndEffects(args)
      : null
  ), [driver]);
  const depositPartyInventoryFromChar = useMemo(() => (
    typeof driver.depositPartyInventoryFromChar === 'function'
      ? (args) => driver.depositPartyInventoryFromChar(args)
      : null
  ), [driver]);
  const takePartyInventoryToChar = useMemo(() => (
    typeof driver.takePartyInventoryToChar === 'function'
      ? (args) => driver.takePartyInventoryToChar(args)
      : null
  ), [driver]);
  const transferInventoryItemBetweenChars = useMemo(() => (
    typeof driver.transferInventoryItemBetweenChars === 'function'
      ? (args) => driver.transferInventoryItemBetweenChars(args)
      : null
  ), [driver]);
  const moveCharItemToCraftingTable = useMemo(() => (
    typeof driver.moveCharItemToCraftingTable === 'function'
      ? (args) => driver.moveCharItemToCraftingTable(args)
      : null
  ), [driver]);
  const addInventoryItem = useMemo(() => (
    typeof driver.addInventoryItem === 'function'
      ? (args) => driver.addInventoryItem(args)
      : null
  ), [driver]);
  const updateInventoryItem = useMemo(() => (
    typeof driver.updateInventoryItem === 'function'
      ? (args) => driver.updateInventoryItem(args)
      : null
  ), [driver]);
  const removeInventoryItem = useMemo(() => (
    typeof driver.removeInventoryItem === 'function'
      ? (args) => driver.removeInventoryItem(args)
      : null
  ), [driver]);
  const setInventoryItemFavorite = useMemo(() => (
    typeof driver.setInventoryItemFavorite === 'function'
      ? (args) => driver.setInventoryItemFavorite(args)
      : null
  ), [driver]);
  const toggleInventoryItemEquipped = useMemo(() => (
    typeof driver.toggleInventoryItemEquipped === 'function'
      ? (args) => driver.toggleInventoryItemEquipped(args)
      : null
  ), [driver]);
  const consumeInventoryItem = useMemo(() => (
    typeof driver.consumeInventoryItem === 'function'
      ? (args) => driver.consumeInventoryItem(args)
      : null
  ), [driver]);
  const activateInventoryItemAbility = useMemo(() => (
    typeof driver.activateInventoryItemAbility === 'function'
      ? (args) => driver.activateInventoryItemAbility(args)
      : null
  ), [driver]);
  const craftOutputToChar = useMemo(() => (
    typeof driver.craftOutputToChar === 'function'
      ? (args) => driver.craftOutputToChar(args)
      : null
  ), [driver]);
  const craftOutputToParty = useMemo(() => (
    typeof driver.craftOutputToParty === 'function'
      ? (args) => driver.craftOutputToParty(args)
      : null
  ), [driver]);
  const tableRowToChar = useMemo(() => (
    typeof driver.tableRowToChar === 'function'
      ? (args) => driver.tableRowToChar(args)
      : null
  ), [driver]);
  const tableRowToParty = useMemo(() => (
    typeof driver.tableRowToParty === 'function'
      ? (args) => driver.tableRowToParty(args)
      : null
  ), [driver]);
  const activateCombatItemAbility = useMemo(() => (
    typeof driver.activateCombatItemAbility === 'function'
      ? (args) => driver.activateCombatItemAbility(args)
      : null
  ), [driver]);
  const applyEffectInstanceLifecycle = useMemo(() => (
    typeof driver.applyEffectInstanceLifecycle === 'function'
      ? (args) => driver.applyEffectInstanceLifecycle(args)
      : null
  ), [driver]);
  const reconcileItemAbilityEffectInstancesForChar = useMemo(() => (
    typeof driver.reconcileItemAbilityEffectInstancesForChar === 'function'
      ? (args) => driver.reconcileItemAbilityEffectInstancesForChar(args)
      : null
  ), [driver]);
  const purchaseShopListing = useMemo(() => (
    typeof driver.purchaseShopListing === 'function'
      ? (args) => driver.purchaseShopListing(args)
      : null
  ), [driver]);
  const checkoutShopCart = useMemo(() => (
    typeof driver.checkoutShopCart === 'function'
      ? (args) => driver.checkoutShopCart(args)
      : null
  ), [driver]);
  const sellInventoryItemToShopForDm = useMemo(() => (
    typeof driver.sellInventoryItemToShopForDm === 'function'
      ? (args) => driver.sellInventoryItemToShopForDm(args)
      : null
  ), [driver]);
  const submitTradeOffer = useMemo(() => (
    typeof driver.submitTradeOffer === 'function'
      ? (args) => driver.submitTradeOffer(args)
      : null
  ), [driver]);
  const cancelTradeOffer = useMemo(() => (
    typeof driver.cancelTradeOffer === 'function'
      ? (args) => driver.cancelTradeOffer(args)
      : null
  ), [driver]);
  const denyTradeOfferForDm = useMemo(() => (
    typeof driver.denyTradeOfferForDm === 'function'
      ? (args) => driver.denyTradeOfferForDm(args)
      : null
  ), [driver]);
  const settleTradeOfferForDm = useMemo(() => (
    typeof driver.settleTradeOfferForDm === 'function'
      ? (args) => driver.settleTradeOfferForDm(args)
      : null
  ), [driver]);
  const promoteTradeReceiptLineToShopListingForDm = useMemo(() => (
    typeof driver.promoteTradeReceiptLineToShopListingForDm === 'function'
      ? (args) => driver.promoteTradeReceiptLineToShopListingForDm(args)
      : null
  ), [driver]);
  const adjustShopWalletForDm = useMemo(() => (
    typeof driver.adjustShopWalletForDm === 'function'
      ? (args) => driver.adjustShopWalletForDm(args)
      : null
  ), [driver]);
  const importShopDataForDm = useMemo(() => (
    typeof driver.importShopDataForDm === 'function'
      ? (args) => driver.importShopDataForDm(args)
      : null
  ), [driver]);
  const createDmShop = useMemo(() => (
    typeof driver.createDmShop === 'function'
      ? (args) => driver.createDmShop(args)
      : null
  ), [driver]);
  const deleteDmShop = useMemo(() => (
    typeof driver.deleteDmShop === 'function'
      ? (args) => driver.deleteDmShop(args)
      : null
  ), [driver]);
  const updateDmShop = useMemo(() => (
    typeof driver.updateDmShop === 'function'
      ? (args) => driver.updateDmShop(args)
      : null
  ), [driver]);
  const createDmShopListing = useMemo(() => (
    typeof driver.createDmShopListing === 'function'
      ? (args) => driver.createDmShopListing(args)
      : null
  ), [driver]);
  const deleteDmShopListing = useMemo(() => (
    typeof driver.deleteDmShopListing === 'function'
      ? (args) => driver.deleteDmShopListing(args)
      : null
  ), [driver]);
  const updateDmShopListing = useMemo(() => (
    typeof driver.updateDmShopListing === 'function'
      ? (args) => driver.updateDmShopListing(args)
      : null
  ), [driver]);
  const updateDmShopRuntimeState = useMemo(() => (
    typeof driver.updateDmShopRuntimeState === 'function'
      ? (args) => driver.updateDmShopRuntimeState(args)
      : null
  ), [driver]);
  const publishLootEncounterForDm = useMemo(() => (
    typeof driver.publishLootEncounterForDm === 'function'
      ? (args) => driver.publishLootEncounterForDm(args)
      : null
  ), [driver]);
  const importLootDataForDm = useMemo(() => (
    typeof driver.importLootDataForDm === 'function'
      ? (args) => driver.importLootDataForDm(args)
      : null
  ), [driver]);
  const claimLootDrop = useMemo(() => (
    typeof driver.claimLootDrop === 'function'
      ? (args) => driver.claimLootDrop(args)
      : null
  ), [driver]);
  const withdrawLootClaim = useMemo(() => (
    typeof driver.withdrawLootClaim === 'function'
      ? (args) => driver.withdrawLootClaim(args)
      : null
  ), [driver]);
  const setLootDropVisibilityForDm = useMemo(() => (
    typeof driver.setLootDropVisibilityForDm === 'function'
      ? (args) => driver.setLootDropVisibilityForDm(args)
      : null
  ), [driver]);
  const revealAllLootDropsForDm = useMemo(() => (
    typeof driver.revealAllLootDropsForDm === 'function'
      ? (args) => driver.revealAllLootDropsForDm(args)
      : null
  ), [driver]);
  const finishLootEncounterForDm = useMemo(() => (
    typeof driver.finishLootEncounterForDm === 'function'
      ? (args) => driver.finishLootEncounterForDm(args)
      : null
  ), [driver]);
  const distributeLootXpForDm = useMemo(() => (
    typeof driver.distributeLootXpForDm === 'function'
      ? (args) => driver.distributeLootXpForDm(args)
      : null
  ), [driver]);
  const awardLootDrop = useMemo(() => (
    typeof driver.awardLootDrop === 'function'
      ? (args) => driver.awardLootDrop(args)
      : null
  ), [driver]);
  const awardCurrencyLoot = useMemo(() => (
    typeof driver.awardCurrencyLoot === 'function'
      ? (args) => driver.awardCurrencyLoot(args)
      : null
  ), [driver]);

  const updateGameData = useCallback(patch => driver.updateMultiple(patch), [driver]);
  const pushAuditEvent = useCallback(event => driver.pushAuditEvent(event), [driver]);
  const pushAuditSession = useCallback(session => driver.pushAuditSession(session), [driver]);

  // Loading is derived from connectionStatus for backward compat. Original hook
  // exposed `loading` as its own state; we keep the derived alias so existing
  // consumers (e.g. `if (loading) return <Spinner/>`) work unchanged.
  const loading = useMemo(() => connectionStatus === 'checking', [connectionStatus]);

  return {
    ...data,
    loading,
    connectionStatus,
    saveChars,
    saveFood,
    saveDiets,
    saveDiseases,
    saveSystems,
    saveFoodTypes,
    saveLocations,
    saveGlobalSettings,
    saveSpellLibrary,
    saveGroupTravelLog,
    saveCurrentLocation,
    saveMessages,
    saveJournal,
    saveDmJournal,
    saveNpcLibrary,
    saveJournalSettings,
    saveCombat,
    saveCombatSnapshots,
    savePartyInventory,
    upsertJournalEntry,
    deleteJournalEntry,
    setJournalEntryCampaignScope,
    upsertDmJournalEntry,
    deleteDmJournalEntry,
    upsertGlobalLoreEntry,
    deleteGlobalLoreEntry,
    lookupJournalEntriesByName,
    lookupDmJournalEntriesByName,
    discoverJournalEntries,
    discoverDmJournalEntries,
    markStatusMessageSeen,
    dismissStatusMessage,
    markDmChatRead,
    sendStatusMessage,
    sendDmChatMessage,
    deleteDmChatMessage,
    patchCharacterVitals,
    patchCharacterSystemInstance,
    patchCharacterSystemState,
    patchCharacterSystemStateAndVitals,
    patchCharacterSystemStateAndResources,
    patchCharacterSystemStateAndEffects,
    depositPartyInventoryFromChar,
    takePartyInventoryToChar,
    transferInventoryItemBetweenChars,
    moveCharItemToCraftingTable,
    addInventoryItem,
    updateInventoryItem,
    removeInventoryItem,
    setInventoryItemFavorite,
    toggleInventoryItemEquipped,
    consumeInventoryItem,
    activateInventoryItemAbility,
    craftOutputToChar,
    craftOutputToParty,
    tableRowToChar,
    tableRowToParty,
    activateCombatItemAbility,
    applyEffectInstanceLifecycle,
    reconcileItemAbilityEffectInstancesForChar,
    purchaseShopListing,
    checkoutShopCart,
    sellInventoryItemToShopForDm,
    submitTradeOffer,
    cancelTradeOffer,
    denyTradeOfferForDm,
    settleTradeOfferForDm,
    promoteTradeReceiptLineToShopListingForDm,
    adjustShopWalletForDm,
    importShopDataForDm,
    createDmShop,
    deleteDmShop,
    updateDmShop,
    createDmShopListing,
    deleteDmShopListing,
    updateDmShopListing,
    updateDmShopRuntimeState,
    publishLootEncounterForDm,
    importLootDataForDm,
    claimLootDrop,
    withdrawLootClaim,
    setLootDropVisibilityForDm,
    revealAllLootDropsForDm,
    finishLootEncounterForDm,
    distributeLootXpForDm,
    awardLootDrop,
    awardCurrencyLoot,
    updateGameData,
    pushAuditEvent,
    pushAuditSession,
  };
};
